import { getMergedActiveCashSession } from '../../cash/infra-api';
import { listBranchAppointments } from '../../appointments/infra/appointmentsRepo';
import { countStockByBranch } from '../../inventory/stock/infra/queries/stockQueries';
import { countOpenOrders } from '../../orders/infra/ordersRepo';
import { fetchRemoteBranchCashSummary, fetchRemoteSalesSnapshot } from '../../reports/infra/reportsRemoteRepo';
import { getSalesSnapshotByDateRange } from '../../sales/public-api';
import { isOnline } from '../../shared/connectivity/connectivityInfra';
import { branchMissingMapLocation } from '../../settings/domain/branchLocationStatus';
import type { BranchAccess } from '../../shell/session/store/sessionTypes';
import type { BranchPanelSnapshot, OwnerPanelPeriod, OwnerPanelSummary } from '../domain/types';
import { countRemoteLowStockByBranch } from '../infra/remoteBranchLowStock';
import { buildOwnerPanelAlerts } from './buildOwnerPanelAlerts';
import { rangesForOwnerPanelPeriod, todayRangeIso } from './dateRanges';

export type LoadOwnerPanelOptions = {
  orgId: string;
  branches: BranchAccess[];
  isOwner: boolean;
  period: OwnerPanelPeriod;
  hasInventory: boolean;
  hasAppointments: boolean;
  cashAllowed: boolean;
  publicStoreOn: boolean;
};

async function loadBranchSalesSnapshot(
  branchId: string,
  fromIso: string,
  toIso: string,
): Promise<{ salesTotal: number; salesCount: number }> {
  const local = await getSalesSnapshotByDateRange(branchId, fromIso, toIso);
  try {
    if (await isOnline()) {
      const remote = await fetchRemoteSalesSnapshot(branchId, fromIso, toIso);
      if (remote) {
        return { salesTotal: remote.salesTotal, salesCount: remote.salesCount };
      }
    }
  } catch {
    // Sin RPC remota: usar SQLite local.
  }
  return { salesTotal: local.salesTotal, salesCount: local.salesCount };
}

async function loadBranchLowStockCount(branchId: string): Promise<number> {
  try {
    if (await isOnline()) {
      const remote = await countRemoteLowStockByBranch(branchId);
      if (remote != null) {
        return remote;
      }
    }
  } catch {
    // Sin remoto: SQLite local.
  }
  return countStockByBranch(branchId, { statusFilter: 'low' });
}

async function loadBranchSnapshot(
  branch: BranchAccess,
  orgId: string,
  options: LoadOwnerPanelOptions,
  salesRange: { from: string; to: string },
  priorRange: { from: string; to: string },
): Promise<BranchPanelSnapshot> {
  const base: BranchPanelSnapshot = {
    branch,
    loaded: false,
    hasError: false,
    salesTotal: null,
    salesCount: null,
    priorSalesTotal: null,
    lowStockCount: null,
    cashOpen: null,
    cashSession: null,
    pendingOrders: null,
    appointmentsToday: null,
    missingMap: branchMissingMapLocation(branch),
  };

  try {
    const [currentSnap, priorSnap] = await Promise.all([
      loadBranchSalesSnapshot(branch.id, salesRange.from, salesRange.to),
      loadBranchSalesSnapshot(branch.id, priorRange.from, priorRange.to),
    ]);
    base.salesTotal = currentSnap.salesTotal;
    base.salesCount = currentSnap.salesCount;
    base.priorSalesTotal = priorSnap.salesTotal;
  } catch {
    // #797: marcar error para advertir totales parciales
    base.hasError = true;
  }

  if (options.cashAllowed) {
    try {
      if (await isOnline()) {
        const remote = await fetchRemoteBranchCashSummary(
          branch.id,
          todayRangeIso().from,
          { force: true },
        );
        if (remote) {
          base.cashSession = remote.activeSession;
          base.cashOpen = remote.activeSession != null;
        } else {
          const session = await getMergedActiveCashSession(branch.id, { force: true });
          base.cashSession = session;
          base.cashOpen = session != null;
        }
      } else {
        const session = await getMergedActiveCashSession(branch.id);
        base.cashSession = session;
        base.cashOpen = session != null;
      }
    } catch {
      base.cashOpen = null;
      base.hasError = true;
    }
  }

  if (options.hasInventory) {
    try {
      base.lowStockCount = await loadBranchLowStockCount(branch.id);
    } catch {
      base.lowStockCount = null;
      base.hasError = true;
    }
  }

  if (options.publicStoreOn) {
    try {
      base.pendingOrders = await countOpenOrders(orgId, branch.id, ['pending']);
    } catch {
      base.pendingOrders = null;
      base.hasError = true;
    }
  }

  if (options.hasAppointments) {
    try {
      const { from, to } = todayRangeIso();
      const rows = await listBranchAppointments(branch.id, from, to, null);
      const active = rows.filter((a) => a.status === 'requested' || a.status === 'confirmed');
      base.appointmentsToday = active.length;
    } catch {
      base.appointmentsToday = null;
      base.hasError = true;
    }
  }

  base.loaded = true;
  return base;
}

export async function loadOwnerPanelData(options: LoadOwnerPanelOptions): Promise<OwnerPanelSummary> {
  const { current, prior, priorLabel } = rangesForOwnerPanelPeriod(options.period);
  if (!options.isOwner) {
    return {
      period: options.period,
      priorLabel,
      branches: [],
      alerts: [],
      totalSales: 0,
      totalSalesCount: 0,
      totalPriorSales: 0,
    };
  }

  const branches = await Promise.all(
    options.branches.map((branch) =>
      loadBranchSnapshot(branch, options.orgId, options, current, prior),
    ),
  );

  const alerts = buildOwnerPanelAlerts(branches);
  const totalSales = branches.reduce((sum, b) => sum + (b.salesTotal ?? 0), 0);
  const totalSalesCount = branches.reduce((sum, b) => sum + (b.salesCount ?? 0), 0);
  const totalPriorSales = branches.reduce((sum, b) => sum + (b.priorSalesTotal ?? 0), 0);

  return {
    period: options.period,
    priorLabel,
    branches,
    alerts,
    totalSales,
    totalSalesCount,
    totalPriorSales,
  };
}
